import { AxiosInstance } from 'axios';
import axiosFhirInstance from 'src/careflow/ph-core/fhir-client/application/adapters/out/instance/axiosFhirInstance';
import {
  Bundle,
  DocumentReference,
  Patient,
  Person,
} from 'src/careflow/ph-core/fhir-client/resources';
import uuidv4 from 'src/utils/uuidv4';
import {
  BundleSourceInput,
  DocumentReferencePayloadParams,
  FuegoFlowContext,
  FuegoWebMessageEnvelope,
  LinkedPatientPayloadParams,
  MainMessage,
  MergeBundleValidationResult,
  MergeMode,
  OutputBundleMode,
  SourcesMap,
  WebMessagePayloadOptions,
} from '../types';

/**
 * Safely decodes a base64 encoded string into a UTF-8 string,
 * working across both browser (atob/TextDecoder) and Node.js environments.
 */
export function decodeBase64ToString(base64Str: string): string {
  try {
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      const binaryString = window.atob(base64Str);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder().decode(bytes);
    }
  } catch (error) {
    // If TextDecoder fails or character issue, fall back
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64Str, 'base64').toString('utf-8');
  }

  return window.atob(base64Str);
}

/**
 * Normalizes any resource or array of resources into a standard FHIR Bundle
 */
export function ensureFhirBundle(raw: any): Bundle {
  if (raw && raw.resourceType === 'Bundle') {
    return raw as Bundle;
  }

  const entries = Array.isArray(raw)
    ? raw.map((resource) => ({ resource }))
    : raw
    ? [{ resource: raw }]
    : [];

  return {
    resourceType: 'Bundle',
    type: 'collection',
    entry: entries,
  } as Bundle;
}

/**
 * Extracts the trailing ID portion from a FHIR reference or URL
 */
export function getIdFromReference(reference?: string | null): string | null {
  if (!reference) return null;
  const parts = reference.split('/');
  return parts.length > 0 ? parts[parts.length - 1] : null;
}

// ============================================================================
// TASK 1: Patient $everything Bundle Fetching
// ============================================================================

/**
 * Fetches the complete FHIR record bundle for a single patient using the
 * HL7 FHIR standard $everything operation: /Patient/{id}/$everything
 *
 * @see https://www.hl7.org/fhir/R4/patient-operation-everything.html
 */
export async function fetchPatientEverythingBundle(
  patientId: string,
  options?: { count?: number; fhirInstance?: AxiosInstance }
): Promise<Bundle> {
  if (!patientId) {
    throw new Error('[fuegoBundleService] patientId is required to fetch $everything bundle');
  }

  const cleanId = patientId.replace(/^Patient\//, '');
  const instance = options?.fhirInstance || axiosFhirInstance;

  const params: Record<string, any> = {};
  if (options?.count !== undefined) {
    params._count = options.count;
  }

  const url = `Patient/${cleanId}/$everything`;
  const response = await instance.get<Bundle>(url, {
    params: Object.keys(params).length > 0 ? params : undefined,
    headers: {
      Accept: 'application/fhir+json, application/json',
    },
  });

  return ensureFhirBundle(response.data);
}

/**
 * Given an array of patient IDs, queries the FHIR server for all related FHIR
 * resources for each patient (via $everything) and returns bundles mapped by patient ID.
 */
export async function fetchPatientsEverythingBundles(
  patientIds: string[],
  options?: { count?: number; fhirInstance?: AxiosInstance }
): Promise<Record<string, Bundle>> {
  if (!patientIds || !Array.isArray(patientIds) || patientIds.length === 0) {
    return {};
  }

  const results: Record<string, Bundle> = {};

  await Promise.all(
    patientIds.map(async (id) => {
      const cleanId = id.replace(/^Patient\//, '');
      const bundle = await fetchPatientEverythingBundle(cleanId, options);
      results[cleanId] = bundle;
    })
  );

  return results;
}

// ============================================================================
// TASK 2: DocumentReference / Binary Bundle Extraction
// ============================================================================

/**
 * Given a DocumentReference or its ID, checks the related Binary attachment,
 * fetches or decodes its content, and parses it into an HL7 FHIR Bundle.
 */
export async function fetchBundleFromDocumentReference(
  docRefOrId: DocumentReference | string,
  options?: { fhirInstance?: AxiosInstance }
): Promise<Bundle> {
  const instance = options?.fhirInstance || axiosFhirInstance;

  let docRef: DocumentReference;
  if (typeof docRefOrId === 'string') {
    const cleanId = docRefOrId.replace(/^DocumentReference\//, '');
    const res = await instance.get<DocumentReference>(`DocumentReference/${cleanId}`, {
      headers: { Accept: 'application/fhir+json, application/json' },
    });
    docRef = res.data;
  } else {
    docRef = docRefOrId;
  }

  if (!docRef || !docRef.content || docRef.content.length === 0) {
    throw new Error(
      `[fuegoBundleService] DocumentReference "${docRef?.id || docRefOrId}" has no content attachments`
    );
  }

  // Find preferred attachment (prioritize FHIR JSON / JSON types)
  const preferredContent =
    docRef.content.find((c) => {
      const mime = c?.attachment?.contentType?.toLowerCase();
      return mime === 'application/fhir+json' || mime === 'application/json';
    }) || docRef.content[0];

  const attachment = preferredContent?.attachment;
  if (!attachment) {
    throw new Error(
      `[fuegoBundleService] No attachment found in DocumentReference "${docRef?.id || docRefOrId}"`
    );
  }

  // Case A: Binary data is present inline as base64 in attachment.data
  if (attachment.data) {
    const jsonStr = decodeBase64ToString(attachment.data);
    const parsed = JSON.parse(jsonStr);
    const bundle = ensureFhirBundle(parsed);
    console.debug(
      '[fuegoBundleService] Successfully extracted bundle from inline attachment binary data:',
      {
        docRefId: docRef?.id || docRefOrId,
        resourceType: bundle.resourceType,
        totalEntries: bundle.entry?.length || 0,
        bundle,
      }
    );
    return bundle;
  }

  // Case B: Attachment points to a Binary URL
  if (attachment.url) {
    const { url } = attachment;
    const binaryId = getIdFromReference(url);

    // Call Binary endpoint
    const binaryEndpoint = binaryId ? `Binary/${binaryId}` : url;
    const binaryResponse = await instance.get(binaryEndpoint, {
      headers: { Accept: 'application/fhir+json, application/json' },
    });

    const responseData = binaryResponse.data;

    // Check if the response is a FHIR Binary resource with base64 data
    if (responseData && responseData.resourceType === 'Binary' && responseData.data) {
      const decoded = decodeBase64ToString(responseData.data);
      const parsed = JSON.parse(decoded);
      const bundle = ensureFhirBundle(parsed);
      console.debug(
        `[fuegoBundleService] Successfully extracted bundle from Binary resource (${binaryId || url}):`,
        {
          docRefId: docRef?.id || docRefOrId,
          binaryId,
          endpoint: binaryEndpoint,
          resourceType: bundle.resourceType,
          totalEntries: bundle.entry?.length || 0,
          bundle,
        }
      );
      return bundle;
    }

    // Check if the server returned raw JSON / Bundle directly
    if (typeof responseData === 'string') {
      try {
        const parsed = JSON.parse(responseData);
        const bundle = ensureFhirBundle(parsed);
        console.debug(
          `[fuegoBundleService] Successfully extracted bundle from raw string Binary response (${binaryId || url}):`,
          {
            docRefId: docRef?.id || docRefOrId,
            binaryId,
            endpoint: binaryEndpoint,
            resourceType: bundle.resourceType,
            totalEntries: bundle.entry?.length || 0,
            bundle,
          }
        );
        return bundle;
      } catch (err) {
        // In case it's a raw base64 string
        const decoded = decodeBase64ToString(responseData);
        const parsed = JSON.parse(decoded);
        const bundle = ensureFhirBundle(parsed);
        console.debug(
          `[fuegoBundleService] Successfully extracted bundle from raw base64 Binary response (${binaryId || url}):`,
          {
            docRefId: docRef?.id || docRefOrId,
            binaryId,
            endpoint: binaryEndpoint,
            resourceType: bundle.resourceType,
            totalEntries: bundle.entry?.length || 0,
            bundle,
          }
        );
        return bundle;
      }
    }

    const bundle = ensureFhirBundle(responseData);
    console.debug(
      `[fuegoBundleService] Successfully extracted bundle directly from Binary response (${binaryId || url}):`,
      {
        docRefId: docRef?.id || docRefOrId,
        binaryId,
        endpoint: binaryEndpoint,
        resourceType: bundle.resourceType,
        totalEntries: bundle.entry?.length || 0,
        bundle,
      }
    );
    return bundle;
  }

  throw new Error(
    `[fuegoBundleService] DocumentReference "${docRef?.id || docRefOrId}" attachment contains neither inline data nor a valid URL`
  );
}

// ============================================================================
// TASK 3: Converting Bundles to webMessagePayload with Primary DataSource
// ============================================================================

/**
 * Synthesizes a FHIR Person resource from a primary Patient resource
 * to populate the header, identifiers, and summary cards in Fuego App.
 */
export function synthesizePersonFromPatient(patient: Patient, patientId?: string): Person {
  const id = patient.id || patientId;
  return {
    resourceType: 'Person',
    name: patient.name,
    identifier: patient.identifier,
    telecom: patient.telecom,
    birthDate: patient.birthDate,
    gender: patient.gender,
    address: patient.address,
    managingOrganization: patient.managingOrganization,
    link: id
      ? [
          {
            target: {
              reference: `Patient/${id}`,
            },
          },
        ]
      : undefined,
  };
}

/**
 * Given HL7 FHIR bundles, converts them into the webMessagePayload expected
 * by Fuego App (MainMessage), with a guaranteed primary data source placed
 * first in the sources map.
 */
export function convertBundlesToWebMessagePayload(
  sourcesInput: BundleSourceInput[],
  options?: WebMessagePayloadOptions
): MainMessage {
  if (!sourcesInput || sourcesInput.length === 0) {
    throw new Error('[fuegoBundleService] At least one bundle source is required to build payload');
  }

  // 1. Determine primary data source
  const explicitPrimaryId =
    options?.primarySourceId ||
    sourcesInput.find((s) => s.isPrimary)?.id;

  const primaryId = explicitPrimaryId || sourcesInput[0]?.id;
  const primarySource = sourcesInput.find((s) => s.id === primaryId) || sourcesInput[0];

  // 2. Build SourcesMap ensuring primary datasource key is FIRST
  // Fuego App DataSourceProvider selects Object.keys(sourcesData)[0] as the active data source.
  const sourcesMap: SourcesMap = {};

  if (primarySource) {
    sourcesMap[primarySource.id] = {
      sourceName: primarySource.sourceName,
      bundle: primarySource.bundle,
    };
  }

  for (const src of sourcesInput) {
    if (src.id !== primarySource.id) {
      sourcesMap[src.id] = {
        sourceName: src.sourceName,
        bundle: src.bundle,
      };
    }
  }

  // 3. Determine Merge Mode:
  // If explicitly provided via options, use it.
  // Otherwise, if a primary data source is designated, default to 'edit', else 'standard'.
  const rawMergeMode = options?.mergeMode || options?.customOptions?.mergeMode;
  const resolvedMergeMode: MergeMode =
    rawMergeMode === 'edit' || rawMergeMode === 'standard'
      ? rawMergeMode
      : explicitPrimaryId
        ? 'edit'
        : 'standard';

  const isEditMode = resolvedMergeMode === 'edit';

  // 4. Synthesize mainPerson if not explicitly provided
  let mainPerson: Person | undefined = options?.mainPerson;
  if (!mainPerson && primarySource?.bundle?.entry) {
    const patientEntry = primarySource.bundle.entry.find(
      (e) => e?.resource?.resourceType === 'Patient'
    );
    if (patientEntry?.resource) {
      mainPerson = synthesizePersonFromPatient(
        patientEntry.resource as Patient,
        primarySource.id
      );
    }
  }

  // Resolve edit-mode flags:
  // If explicitly specified in options, use that value.
  // In 'edit' mode, default to true if omitted.
  const lockFlag =
    options?.lockPrimaryRelatedResourcesOnMerge ??
    options?.customOptions?.lockPrimaryRelatedResourcesOnMerge ??
    (isEditMode ? true : undefined);

  const keepIdsFlag =
    options?.keepOriginalResourceIdsOnMerge ??
    options?.customOptions?.keepOriginalResourceIdsOnMerge ??
    (isEditMode ? true : undefined);

  const keepDataFlag =
    options?.keepDataNotSelectableOnMerge ??
    options?.customOptions?.keepDataNotSelectableOnMerge ??
    (isEditMode ? true : undefined);

  const returnOnlyFlag =
    options?.returnOnlyModifiedAndNewResourcesOnMerge ??
    options?.customOptions?.returnOnlyModifiedAndNewResourcesOnMerge ??
    (isEditMode ? true : undefined);

  const rawOutputBundleMode =
    options?.outputBundleMode ||
    options?.customOptions?.outputBundleMode;
  const resolvedOutputBundleMode: OutputBundleMode =
    rawOutputBundleMode === 'collection' ? 'collection' : 'transaction';

  return {
    transactionId: options?.transactionId || uuidv4(),
    mainPerson,
    primarySourceId: explicitPrimaryId || primaryId,
    sources: sourcesMap,
    options: {
      iframe: true,
      mergeMode: resolvedMergeMode,
      outputBundleMode: resolvedOutputBundleMode,
      primarySourceId: explicitPrimaryId || primaryId,
      ...(lockFlag !== undefined && {
        lockPrimaryRelatedResourcesOnMerge: lockFlag,
        lockPrimaryRelatedResources: lockFlag,
      }),
      ...(keepIdsFlag !== undefined && {
        keepOriginalResourceIdsOnMerge: keepIdsFlag,
        keepOriginalResourceIds: keepIdsFlag,
      }),
      ...(keepDataFlag !== undefined && {
        keepDataNotSelectableOnMerge: keepDataFlag,
        preservePrimaryPatientProperties: keepDataFlag,
      }),
      ...(returnOnlyFlag !== undefined && {
        returnOnlyModifiedAndNewResourcesOnMerge: returnOnlyFlag,
      }),
      ...(options?.hideSuccessfulMergeScreen !== undefined && {
        hideSuccessfulMergeScreen: options.hideSuccessfulMergeScreen,
      }),
      ...options?.customOptions,
    },
    loadStatus: options?.loadStatus,
  };
}

// ============================================================================
// TASK 4: webMessagePayload for Current Patient & Linked Patient Profile
// ============================================================================

/**
 * Generates the webMessagePayload using all the data on the current patient
 * (via Patient.id and $everything) and the selected linked patient (via Patient.link[x]).
 */
export async function generatePayloadForLinkedPatients(
  params: LinkedPatientPayloadParams
): Promise<MainMessage> {
  const { currentPatientId, linkedPatientId, primaryPatientId } = params;

  if (!currentPatientId || !linkedPatientId) {
    throw new Error(
      '[fuegoBundleService] Both currentPatientId and linkedPatientId are required for linked patient payload'
    );
  }

  const cleanCurrentId = currentPatientId.replace(/^Patient\//, '');
  const cleanLinkedId = linkedPatientId.replace(/^Patient\//, '');
  const effectivePrimaryId = (primaryPatientId || cleanCurrentId).replace(/^Patient\//, '');

  // Concurrently fetch $everything bundles for both patients
  const bundlesMap = await fetchPatientsEverythingBundles([cleanCurrentId, cleanLinkedId]);

  const currentBundle = bundlesMap[cleanCurrentId];
  const linkedBundle = bundlesMap[cleanLinkedId];

  const currentSourceLabel = params.currentPatientName
    ? `${params.currentPatientName} (Careflow)`
    : 'Current Patient Record';

  const linkedSourceLabel = params.linkedPatientName
    ? `${params.linkedPatientName} (${cleanLinkedId})`
    : `Linked Patient (${cleanLinkedId})`;

  const sourcesInput: BundleSourceInput[] = [
    {
      id: cleanCurrentId,
      sourceName: currentSourceLabel,
      bundle: currentBundle,
      isPrimary: effectivePrimaryId === cleanCurrentId,
    },
    {
      id: cleanLinkedId,
      sourceName: linkedSourceLabel,
      bundle: linkedBundle,
      isPrimary: effectivePrimaryId === cleanLinkedId,
    },
  ];

  return convertBundlesToWebMessagePayload(sourcesInput, {
    primarySourceId: effectivePrimaryId,
    mergeMode: params.options?.mergeMode,
    lockPrimaryRelatedResourcesOnMerge: params.options?.lockPrimaryRelatedResourcesOnMerge,
    keepOriginalResourceIdsOnMerge: params.options?.keepOriginalResourceIdsOnMerge,
    keepDataNotSelectableOnMerge: params.options?.keepDataNotSelectableOnMerge,
    returnOnlyModifiedAndNewResourcesOnMerge: params.options?.returnOnlyModifiedAndNewResourcesOnMerge,
    customOptions: params.options,
  });
}

// ============================================================================
// TASK 5: webMessagePayload for Current Patient & DocumentReference Profile
// ============================================================================

/**
 * Generates the webMessagePayload using all the data on the current Patient
 * (Patient.id and $everything) and the patient bundle profile stored in the
 * selected DocumentReference attachment (DocumentReference[x]).
 */
export async function generatePayloadForPatientDocument(
  params: DocumentReferencePayloadParams
): Promise<MainMessage> {
  const { currentPatientId, docRefOrId } = params;

  if (!currentPatientId) {
    throw new Error(
      '[fuegoBundleService] currentPatientId is required for patient document payload'
    );
  }

  if (!docRefOrId) {
    throw new Error(
      '[fuegoBundleService] docRefOrId is required for patient document payload'
    );
  }

  const cleanPatientId = currentPatientId.replace(/^Patient\//, '');

  // Concurrently fetch the patient's $everything bundle and the DocumentReference bundle
  const [patientBundle, docBundle] = await Promise.all([
    fetchPatientEverythingBundle(cleanPatientId, {
      fhirInstance: params.options?.fhirInstance,
    }),
    fetchBundleFromDocumentReference(docRefOrId, {
      fhirInstance: params.options?.fhirInstance,
    }),
  ]);

  const docId =
    typeof docRefOrId === 'string'
      ? docRefOrId.replace(/^DocumentReference\//, '')
      : docRefOrId.id || 'document-reference';

  const docTitle =
    params.documentTitle ||
    (typeof docRefOrId !== 'string'
      ? docRefOrId.content?.[0]?.attachment?.title || docRefOrId.description
      : undefined) ||
    `Attachment (${docId})`;

  const currentSourceLabel = params.currentPatientName
    ? `${params.currentPatientName} (Careflow)`
    : 'Current Patient Record';

  const sourcesInput: BundleSourceInput[] = [
    {
      id: cleanPatientId,
      sourceName: currentSourceLabel,
      bundle: patientBundle,
      isPrimary: true,
    },
    {
      id: docId,
      sourceName: docTitle,
      bundle: docBundle,
      isPrimary: false,
    },
  ];

  const message = convertBundlesToWebMessagePayload(sourcesInput, {
    primarySourceId: cleanPatientId,
    mergeMode: params.options?.mergeMode,
    lockPrimaryRelatedResourcesOnMerge: params.options?.lockPrimaryRelatedResourcesOnMerge,
    keepOriginalResourceIdsOnMerge: params.options?.keepOriginalResourceIdsOnMerge,
    keepDataNotSelectableOnMerge: params.options?.keepDataNotSelectableOnMerge,
    returnOnlyModifiedAndNewResourcesOnMerge: params.options?.returnOnlyModifiedAndNewResourcesOnMerge,
    customOptions: params.options,
  });

  console.debug('[generatePayloadForPatientDocument] Output message:', message);

  return message;
}

/**
 * Assembles and generates the complete FHIR webMessagePayload
 * based on the provided FuegoFlowContext.
 */
export async function generatePayloadForContext(
  context: FuegoFlowContext
): Promise<MainMessage | undefined> {
  if (context.preloadedPayload) {
    return context.preloadedPayload;
  }

  if (context.target === 'patient' && context.primaryPatientId && context.targetPatientId) {
    return generatePayloadForLinkedPatients({
      currentPatientId: context.primaryPatientId,
      linkedPatientId: context.targetPatientId,
      currentPatientName: context.currentPatientName,
      linkedPatientName: context.targetPatientName,
      options: context.options,
    });
  }

  if (
    context.target === 'documentReference' &&
    context.primaryPatientId &&
    context.documentReference
  ) {
    return generatePayloadForPatientDocument({
      currentPatientId: context.primaryPatientId,
      docRefOrId: context.documentReference,
      currentPatientName: context.currentPatientName,
      options: context.options,
    });
  }

  if (context.primaryPatientId) {
    const singleBundle = await fetchPatientEverythingBundle(context.primaryPatientId);
    return convertBundlesToWebMessagePayload(
      [
        {
          id: context.primaryPatientId,
          sourceName: context.currentPatientName || 'Patient Record',
          bundle: singleBundle,
          isPrimary: true,
        },
      ],
      {
        primarySourceId: context.primaryPatientId,
        mergeMode: context.options?.mergeMode,
        lockPrimaryRelatedResourcesOnMerge: context.options?.lockPrimaryRelatedResourcesOnMerge,
        keepOriginalResourceIdsOnMerge: context.options?.keepOriginalResourceIdsOnMerge,
        keepDataNotSelectableOnMerge: context.options?.keepDataNotSelectableOnMerge,
        returnOnlyModifiedAndNewResourcesOnMerge: context.options?.returnOnlyModifiedAndNewResourcesOnMerge,
        customOptions: context.options,
      }
    );
  }

  return undefined;
}

/**
 * Helper to construct the complete Fuego App postMessage envelope
 */
export function buildFuegoWebMessage(data: MainMessage): FuegoWebMessageEnvelope {
  return {
    topic: 'iframe-fuego-app',
    data,
  };
}

/**
 * Validates the in-memory structure of an HL7 FHIR R4 Transaction Bundle emitted by Fuego App.
 *
 * Rules:
 * 1. Must be a valid object with resourceType === 'Bundle'
 * 2. Must have type === 'transaction'
 * 3. Must specify a valid, non-empty id (the output bundle UUID)
 * 4. Must contain an 'entry' array with AT LEAST 1 item (empty entry is rejected)
 * 5. Must strictly OMIT bundle.total (FHIR R4 invariant bdl-1: total is only allowed for 'searchset' and 'history')
 * 6. Every entry must contain:
 *    - resource with resourceType and id
 *    - request with method === 'PUT' and url === '${resourceType}/${id}'
 *    - fullUrl must be omitted (undefined)
 */
export function validateMergeBundleStructure(bundle: unknown): MergeBundleValidationResult {
  if (!bundle || typeof bundle !== 'object') {
    return { isValid: false, error: 'Bundle payload is null or not an object' };
  }

  const b = bundle as Bundle;

  if (b.resourceType !== 'Bundle') {
    return {
      isValid: false,
      error: `Invalid resourceType: expected 'Bundle', received '${(bundle as any).resourceType}'`,
    };
  }

  if (b.type !== 'transaction') {
    return {
      isValid: false,
      error: `Invalid Bundle type: expected 'transaction', received '${b.type}'`,
    };
  }

  if (!b.id || typeof b.id !== 'string' || b.id.trim() === '') {
    return {
      isValid: false,
      error: 'Bundle is missing a required logical ID (UUID)',
    };
  }

  if (!Array.isArray(b.entry) || b.entry.length === 0) {
    return {
      isValid: false,
      bundleId: b.id,
      error: "Bundle 'entry' must be a non-empty array containing at least 1 resource entry",
    };
  }

  // Guardrail: Bundle.total is prohibited per FHIR invariant bdl-1 ("total.empty() or (type = 'searchset') or (type = 'history')")
  if ((b as any).total !== undefined) {
    return {
      isValid: false,
      bundleId: b.id,
      error: `Bundle.total is prohibited for type '${b.type}' per FHIR invariant bdl-1 ("total.empty() or (type = 'searchset') or (type = 'history')")`,
    };
  }

  // Validate individual transaction entries
  for (let i = 0; i < b.entry.length; i++) {
    const entry = b.entry[i];
    if (!entry.resource) {
      return {
        isValid: false,
        bundleId: b.id,
        error: `Bundle entry[${i}] is missing a 'resource' object`,
      };
    }

    const { resourceType, id } = entry.resource;
    if (!resourceType || !id) {
      return {
        isValid: false,
        bundleId: b.id,
        error: `Bundle entry[${i}] resource must specify both 'resourceType' and 'id'`,
      };
    }

    if (!entry.request) {
      return {
        isValid: false,
        bundleId: b.id,
        error: `Bundle entry[${i}] (${resourceType}/${id}) is missing a 'request' object required for transactions`,
      };
    }

    if (entry.request.method !== 'PUT') {
      return {
        isValid: false,
        bundleId: b.id,
        error: `Bundle entry[${i}] (${resourceType}/${id}) request method must be 'PUT', received '${entry.request.method}'`,
      };
    }

    const expectedUrl = `${resourceType}/${id}`;
    if (entry.request.url !== expectedUrl) {
      return {
        isValid: false,
        bundleId: b.id,
        error: `Bundle entry[${i}] request url mismatch: expected '${expectedUrl}', received '${entry.request.url}'`,
      };
    }

    // Guardrail: entry.fullUrl must be omitted per transaction plan
    if ((entry as any).fullUrl !== undefined) {
      return {
        isValid: false,
        bundleId: b.id,
        error: `Bundle entry[${i}] must omit 'fullUrl' per transaction output specification`,
      };
    }
  }

  return { isValid: true, bundleId: b.id };
}

/**
 * Helper to parse OperationOutcome or Axios error payloads into readable messages.
 */
export function extractFhirErrorMessage(error: any): string {
  if (!error) return 'Unknown FHIR communication error';

  // Check for FHIR OperationOutcome in error response
  const responseData = error.response?.data || error;
  if (responseData?.resourceType === 'OperationOutcome' && Array.isArray(responseData.issue)) {
    const issues = responseData.issue
      .map((iss: any) => iss.diagnostics || iss.details?.text || iss.code)
      .filter(Boolean);
    if (issues.length > 0) {
      return `FHIR Validation Failure: ${issues.join('; ')}`;
    }
  }

  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.message) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Failed to communicate with FHIR server';
}

/**
 * Saves and validates the intermediate merge state bundle on the FHIR server.
 * Uses HTTP PUT on /Bundle/{id} to persist and validate the Bundle resource
 * without executing the transaction entries.
 */
export async function saveAndValidateMergeBundle(
  bundle: Bundle,
  options?: { fhirInstance?: AxiosInstance }
): Promise<Bundle> {
  // 1. In-memory structural validation
  const structCheck = validateMergeBundleStructure(bundle);
  if (!structCheck.isValid) {
    throw new Error(`[fuegoBundleService] Validation Error: ${structCheck.error}`);
  }

  const cleanBundleId = bundle.id!.replace(/^Bundle\//, '');
  const instance = options?.fhirInstance || axiosFhirInstance;

  console.debug('[fuegoBundleService] Validating and saving intermediate Bundle to FHIR:', {
    bundleId: cleanBundleId,
    type: bundle.type,
    entriesCount: bundle.entry?.length || 0,
  });

  try {
    // 2. HTTP PUT to /Bundle/{id} to validate and persist the Bundle resource
    // Defensively ensure bundle.total is stripped per FHIR invariant bdl-1
    const { total: _omittedTotal, ...bundleToSend } = (bundle || {}) as any;

    const response = await instance.put<Bundle>(`Bundle/${cleanBundleId}`, bundleToSend, {
      headers: {
        'Content-Type': 'application/fhir+json; charset=UTF-8',
        Accept: 'application/fhir+json, application/json',
      },
    });

    console.debug('[fuegoBundleService] Successfully saved & validated Bundle on FHIR server:', {
      bundleId: cleanBundleId,
      status: response.status,
    });

    return response.data;
  } catch (error: any) {
    const errorDetails = extractFhirErrorMessage(error);
    console.error('[fuegoBundleService] FHIR server rejected Bundle validation:', errorDetails);
    throw new Error(errorDetails);
  }
}

/**
 * Applies an HL7 FHIR R4 Transaction Bundle to the FHIR server atomically.
 * Issues HTTP POST to the FHIR base URL ('').
 *
 * @param bundle The transaction Bundle to execute.
 * @param options Axios instance or configuration options.
 * @returns The FHIR transaction-response Bundle.
 */
export async function applyMergeTransactionBundle(
  bundle: Bundle,
  options?: { fhirInstance?: AxiosInstance }
): Promise<Bundle> {
  const instance = options?.fhirInstance || axiosFhirInstance;

  console.debug('[fuegoBundleService] Executing FHIR transaction bundle at root:', {
    bundleId: bundle.id,
    type: bundle.type,
    entriesCount: bundle.entry?.length || 0,
  });

  try {
    // HL7 FHIR R4: Transaction bundles are executed via HTTP POST to the FHIR base endpoint ('')
    // Defensively ensure bundle.total is stripped per FHIR invariant bdl-1
    const { total: _omittedTotal, ...bundleToSend } = (bundle || {}) as any;

    const response = await instance.post<Bundle>('', bundleToSend, {
      headers: {
        'Content-Type': 'application/fhir+json; charset=UTF-8',
        Accept: 'application/fhir+json, application/json',
      },
    });

    const responseBundle = response.data;

    // Check for transaction-response integrity
    if (responseBundle?.resourceType === 'Bundle' && responseBundle.type === 'transaction-response') {
      const failedEntries = (responseBundle.entry || []).filter((e) => {
        const status = e.response?.status || '';
        const statusCode = parseInt(status.split(' ')[0], 10);
        return statusCode >= 400;
      });

      if (failedEntries.length > 0) {
        const errorDescriptions = failedEntries
          .map((e) => `${e.response?.location || 'Resource'}: ${e.response?.status}`)
          .join('; ');
        throw new Error(`Transaction partially failed: ${errorDescriptions}`);
      }
    }

    console.debug('[fuegoBundleService] FHIR transaction bundle executed successfully:', {
      bundleId: bundle.id,
      responseType: responseBundle?.type,
    });

    return responseBundle;
  } catch (error: any) {
    const errorDetails = extractFhirErrorMessage(error);
    console.error('[fuegoBundleService] FHIR transaction execution error:', errorDetails);
    throw new Error(errorDetails);
  }
}


